import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Header } from "../../../components/header";
import { CardHeader } from "../../../components/cards";
import { SendIcon } from "../../../components/icons";
import { Select, Textarea } from "../../../components/form";
import { Button } from "../../../components/button";
import { useBulkImportCustomers } from "../hooks";
import { useCategories } from "../../categories/hooks";
import { CustomerStatus, FindBySource } from "../../../__generated__/globalTypes";
import { customerStatusLabel, findBySourceLabel } from "../../../helpers/enum-labels";

type ImportFormat = "pagesJaunes" | "googleMaps";

const PLACEHOLDERS: Record<ImportFormat, string> = {
  pagesJaunes: `[
  {
    "nom": "Boulangerie Martin",
    "telephone": "0493123456",
    "email": "contact@boulangerie-martin.fr",
    "site": "https://boulangerie-martin.fr",
    "adresse": "chemin Launes 06910 Aiglun",
    "url": "https://pagesjaunes.fr/..."
  }
]`,
  googleMaps: `[
  {
    "name": "Sud Est Désinsectisation Nice",
    "rating": "4,9",
    "reviews": "46",
    "address": " · 186 Av. de Fabron",
    "phone": "04 83 93 92 90",
    "website": "https://www.propest.fr/",
    "lat": "43.6900917",
    "lng": "7.1048634",
    "isSponsored": false
  }
]`,
};

export const ImportCustomers: React.FC = () => {
  const navigate = useNavigate();
  const { data: categoriesData } = useCategories();
  const [mutate, { loading }] = useBulkImportCustomers();

  const [rawLeads, setRawLeads] = useState("");
  const [format, setFormat] = useState<ImportFormat>("pagesJaunes");
  const [categoryId, setCategoryId] = useState("");
  const [findBy, setFindBy] = useState("");
  const [status, setStatus] = useState("");
  const [result, setResult] = useState<{ createdCount: number; skippedCount: number } | null>(
    null
  );

  const handleImport = async () => {
    if (loading) return;

    let leads;
    try {
      leads = JSON.parse(rawLeads);
      if (!Array.isArray(leads)) throw new Error();
    } catch {
      toast.error("Le contenu collé n'est pas un JSON valide (tableau de leads attendu)");
      return;
    }

    try {
      const { data } = await mutate({
        variables: {
          input: {
            // Le format Google Maps est envoyé brut, sans transformation (§36).
            ...(format === "googleMaps"
              ? { googleMapsLeads: leads }
              : { leads }),
            categoryId: categoryId ? +categoryId : null,
            findBy: (findBy as FindBySource) || null,
            status: (status as CustomerStatus) || null,
          },
        },
      });

      if (data?.bulkImportCustomers?.ok) {
        setResult({
          createdCount: data.bulkImportCustomers.createdCount ?? 0,
          skippedCount: data.bulkImportCustomers.skippedCount ?? 0,
        });
        toast.success("Import terminé");
      } else {
        toast.error(data?.bulkImportCustomers?.error ?? "Une erreur est survenue");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Une erreur est survenue");
    }
  };

  return (
    <>
      <Header
        title="Import de clients"
        subtitle="Importer un lot de prospects scrapés (Pages Jaunes ou Google Maps)"
        buttons={[
          {
            actionText: "Annuler",
            bgColor: "indigo",
            textColor: "white",
            link: `/customers`,
            icon: <SendIcon />,
          },
        ]}
      />
      <div className="main-container">
        <div className="card">
          <CardHeader title="1. Réglages appliqués à tout le lot" />
          <div className="flex flex-wrap gap-3">
            <Select
              label="Format du fichier"
              className="w-full"
              value={format}
              onChange={(e) => setFormat(e.target.value as ImportFormat)}
            >
              <option value="pagesJaunes">Pages Jaunes (clés françaises)</option>
              <option value="googleMaps">Google Maps (export brut)</option>
            </Select>
            <Select
              label="Catégorie (optionnel)"
              className="w-full"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">-</option>
              {categoriesData?.categories?.results?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
            <Select
              label="Trouvé via (optionnel)"
              className="w-full"
              value={findBy}
              onChange={(e) => setFindBy(e.target.value)}
            >
              <option value="">-</option>
              {Object.keys(FindBySource).map((key) => (
                <option key={key} value={key}>
                  {findBySourceLabel(key)}
                </option>
              ))}
            </Select>
            <Select
              label="Statut (optionnel, défaut Prospect)"
              className="w-full"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">-</option>
              {Object.keys(CustomerStatus).map((key) => (
                <option key={key} value={key}>
                  {customerStatusLabel(key)}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="card mt-4">
          <CardHeader title="2. Coller le JSON scrapé" />
          <Textarea
            className="w-full font-mono text-xs"
            rows={12}
            value={rawLeads}
            onChange={(e) => setRawLeads(e.target.value)}
            placeholder={PLACEHOLDERS[format]}
          />
          {format === "googleMaps" ? (
            <p className="text-xs text-gray-500 dark:text-zinc-400 mt-2">
              Collez l'export Google Maps <strong>tel quel</strong>, sans retouche : les champs{" "}
              <code>rating</code>, <code>reviews</code>, <code>lat</code>, <code>lng</code> et{" "}
              <code>isSponsored</code> sont acceptés puis ignorés. Le préfixe «&nbsp;·&nbsp;» des
              adresses est nettoyé automatiquement, et les liens publicitaires ne sont pas
              enregistrés comme site. Ce format ne contenant ni code postal ni ville, ces champs
              resteront vides. Un client déjà présent avec le même nom sera ignoré, pas dupliqué.
            </p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-zinc-400 mt-2">
              Les clés (<code>nom</code>, <code>telephone</code>, <code>email</code>,{" "}
              <code>site</code>, <code>adresse</code>) sont reprises telles quelles, sans
              transformation. Un client déjà présent avec le même nom sera ignoré, pas dupliqué —
              vous pouvez relancer l'import sur un fichier partiellement déjà traité sans risque.
            </p>
          )}

          <div className="w-full mt-4">
            <Button onClick={handleImport} loading={loading} disabled={loading || !rawLeads}>
              Importer
            </Button>
          </div>
        </div>

        {result && (
          <div className="card mt-4">
            <CardHeader title="Résultat" />
            <div className="flex gap-6">
              <div>
                <p className="text-2xl font-bold text-green-600">{result.createdCount}</p>
                <p className="text-sm text-gray-500 dark:text-zinc-400">Clients créés</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{result.skippedCount}</p>
                <p className="text-sm text-gray-500 dark:text-zinc-400">
                  Ignorés (nom déjà existant)
                </p>
              </div>
            </div>
            <div className="w-full mt-4">
              <Button variant="secondary" onClick={() => navigate("/customers")}>
                Voir la liste des clients
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
